<!--
  应用管理（第三方应用配置）
  对应数据表：mini_apps
-->
<template>
  <div>
    <PageHeaderLayout>
      <div class='main-page-content'>
        <!-- 页面说明 -->
        <div class='page-intro'>
          <div class='page-intro__title'>应用管理</div>
          <div class='page-intro__desc'>
            维护第三方应用（钉钉 / 微信小程序）的接入配置：录入与修改应用名称、应用编码及应用配置（json），支持启用、停用与删除；
            启用状态的应用配置可供业务端按应用编码读取使用。
          </div>
        </div>
        <!-- 查询搜索 -->
        <div class='table-search'>
          <el-form :model='searchCondition' inline size='mini'>
            <el-form-item label='应用类型'>
              <el-select v-model='searchCondition.type' placeholder='全部' clearable style='width: 140px;'>
                <el-option v-for='item in typeOptions' :key='item.id' :label='item.text' :value='item.id' />
              </el-select>
            </el-form-item>
            <el-form-item label='应用名称'>
              <el-input v-model='searchCondition.name' placeholder='应用名称' clearable></el-input>
            </el-form-item>
            <el-form-item label='应用编码'>
              <el-input v-model='searchCondition.code' placeholder='应用编码' clearable></el-input>
            </el-form-item>
            <el-form-item label='状态'>
              <el-select v-model='searchCondition.state' placeholder='全部' clearable style='width: 120px;'>
                <el-option v-for='item in stateOptions' :key='item.id' :label='item.text' :value='item.id' />
              </el-select>
            </el-form-item>
            <el-form-item>
              <el-button type='primary' icon='el-icon-search' :disabled='loading' :loading='loading'
                         @click='handleQuery'>搜索
              </el-button>
            </el-form-item>
            <el-form-item>
              <el-button icon='el-icon-refresh' @click='handleReset'>重置</el-button>
            </el-form-item>
            <el-form-item>
              <el-button type='primary' icon='el-icon-plus' v-hasAuthority="'mini_app_create'"
                         @click='handleAdd'>新增
              </el-button>
            </el-form-item>
          </el-form>
        </div>
        <!-- 渲染表格 -->
        <div class='default-table'>
          <MlPageTable ref='table'
                       :all-columns='defaultColumns'
                       :table-name='tableName'
                       :data-list='dataList'
                       :limit.sync='pageData.page_size'
                       :page.sync='pageData.current_page'
                       :total.sync='total'
                       :height='height'
                       highlight-current-row
                       :row-class-name='tableRowClassName'
                       @row-click='rowClickEvent'
                       @getList='getList'>
            <el-table-column align='center' width='260' label='操作'>
              <template slot-scope='{row,$index}'>
                <el-button type='text' icon='el-icon-view' @click='handleViewConfig(row)'>配置详情</el-button>
                <el-button type='text' icon='el-icon-edit' v-hasAuthority="'mini_app_edit'"
                           @click='handleEdit(row)'>修改
                </el-button>
                <el-button type='text' v-hasAuthority="'mini_app_state'"
                           :icon="row.state === 1 ? 'el-icon-turn-off' : 'el-icon-open'"
                           @click='handleChangeState(row)'>{{ row.state === 1 ? '停用' : '启用' }}
                </el-button>
                <el-button type='text' icon='el-icon-delete' v-hasAuthority="'mini_app_delete'"
                           @click='handleDelete(row,$index)'>删除
                </el-button>
              </template>
            </el-table-column>
          </MlPageTable>
        </div>
      </div>
      <!-- 新增/修改 -->
      <el-dialog :title='title' :visible.sync='open' width='680px' append-to-body>
        <el-form ref='form' :model='form' :rules='rules' label-width='100px' size='mini'>
          <el-form-item label='应用类型' prop='type'>
            <el-select v-model='form.type' placeholder='请选择应用类型' style='width: 100%;'
                       @change='handleTypeChange'>
              <el-option v-for='item in typeOptions' :key='item.id' :label='item.text' :value='item.id' />
            </el-select>
          </el-form-item>
          <el-form-item label='应用名称' prop='name'>
            <el-input v-model='form.name' placeholder='请输入应用名称' clearable></el-input>
          </el-form-item>
          <el-form-item label='应用编码' prop='code'>
            <el-input v-model='form.code' placeholder='应用唯一标识，如 INQUIRY（同类型下唯一）' clearable
                      @input='handleCodeInput'></el-input>
          </el-form-item>
          <el-form-item label='状态' prop='state'>
            <el-radio-group v-model='form.state'>
              <el-radio v-for='item in stateOptions' :key='item.id' :label='item.id'>{{ item.text }}</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label='应用配置'>
            <div class='mb8'>
              <el-button size='mini' icon='el-icon-plus' @click='addConfigRow'>添加配置项</el-button>
              <el-button size='mini' icon='el-icon-magic-stick' @click='applyConfigFields'>填充常用配置项</el-button>
              <el-button size='mini' @click='toggleJsonMode'>{{ jsonMode ? '表单编辑' : 'JSON编辑' }}</el-button>
            </div>
            <template v-if='jsonMode'>
              <el-input v-model='jsonText' type='textarea' :rows='8'
                        placeholder='请输入 json 对象，例如：{"app_key": "xxx"}'></el-input>
            </template>
            <template v-else>
              <div v-for='(row, index) in configRows' :key='index' class='config-row'>
                <el-input v-model='row.key' placeholder='配置项，如 app_key'
                          style='width: 45%;margin-right: 8px;'></el-input>
                <el-input v-model='row.value' placeholder='配置值' style='width: 45%;margin-right: 8px;'></el-input>
                <el-button type='text' icon='el-icon-delete' @click='removeConfigRow(index)'>删除</el-button>
              </div>
            </template>
            <div class='config-tip'>应用配置以 json 存储，支持自定义配置项；业务端按应用编码(code)读取使用。</div>
          </el-form-item>
        </el-form>
        <div slot='footer'>
          <el-button @click='open = false'>取 消</el-button>
          <el-button type='primary' :loading='loadingCommit' @click='submitForm'>确 定</el-button>
        </div>
      </el-dialog>
      <!-- 配置详情 -->
      <el-dialog title='应用配置详情' :visible.sync='openConfig' width='680px' append-to-body>
        <div class='mb8'>
          <span class='config-tip' style='margin-right: 10px;'>应用：{{ currentConfig.name }}（{{ currentConfig.code }}）</span>
          <el-button size='mini' @click='showSecret = !showSecret'>
            {{ showSecret ? '隐藏敏感信息' : '显示敏感信息' }}
          </el-button>
        </div>
        <pre class='config-pre'>{{ configText }}</pre>
      </el-dialog>
    </PageHeaderLayout>
  </div>
</template>

<script>
import MlPageTable from '@/components/common/MlPageTable'
import PageHeaderLayout from '@/layouts/PageHeaderLayout'

export default {
  name: 'MiniAppList',
  components: { PageHeaderLayout, MlPageTable },
  data() {
    return {
      tableName: 'mini_app',
      loading: false,
      loadingCommit: false,
      dataList: [],
      total: 0,
      // 页面新增了顶部说明区块，表格高度相应下调，避免出现页面整体滚动
      height: 740,
      searchCondition: {
        type: null,
        name: null,
        code: null,
        state: null
      },
      pageData: { current_page: 1, page_size: 100 },
      orderBy: { id: 'desc' },
      // 应用类型 / 状态 / 配置参考字段
      typeOptions: [],
      stateOptions: [],
      configFields: {},
      defaultColumns: [
        { hide: false, prop: 'id', label: 'ID', width: 80, sortable: false, fixed: false },
        { hide: false, prop: 'type_text', label: '应用类型', width: 100, sortable: false, fixed: false },
        { hide: false, prop: 'name', label: '应用名称', width: 160, sortable: false, fixed: false },
        { hide: false, prop: 'code', label: '应用编码', width: 140, sortable: false, fixed: false },
        { hide: false, prop: 'config_keys', label: '配置项', width: 220, sortable: false, fixed: false },
        { hide: false, prop: 'state_text', label: '状态', width: 90, sortable: false, fixed: false },
        { hide: false, prop: 'created_at', label: '创建时间', width: 160, sortable: false, fixed: false },
        { hide: false, prop: 'updated_at', label: '修改时间', width: 160, sortable: false, fixed: false }
      ],
      // 弹出层标题
      title: '新增应用',
      // 是否显示弹出层
      open: false,
      // 表单校验
      rules: {
        type: [{ required: true, message: '应用类型不能为空', trigger: 'change' }],
        name: [{ required: true, message: '应用名称不能为空', trigger: 'blur' }],
        code: [{ required: true, message: '应用编码不能为空', trigger: 'blur' }]
      },
      // 表单参数
      form: {},
      // 配置项（表单模式）
      configRows: [],
      // 是否使用 json 模式编辑配置
      jsonMode: false,
      jsonText: '',
      // 配置详情
      openConfig: false,
      currentConfig: {},
      showSecret: false,
      // 筛选条件缓存key
      pageSearchKey: 'page_search_key:mini_app'
    }
  },
  computed: {
    configText() {
      const config = this.currentConfig.config || {}
      const result = {}
      Object.keys(config).forEach((key) => {
        result[key] = this.maskValue(key, config[key])
      })
      return JSON.stringify(result, null, 2)
    }
  },
  methods: {
    // eslint-disable-next-line no-unused-vars
    rowClickEvent(row, column, event) {
    },
    // eslint-disable-next-line no-unused-vars
    tableRowClassName({ row, rowIndex }) {
      return 'default-table-row'
    },
    // 敏感信息脱敏
    maskValue(key, value) {
      if (this.showSecret || value === null || value === undefined) return value
      return /(secret|token|password|key)/i.test(key) && value !== '' ? '******' : value
    },
    // 编码统一大写
    handleCodeInput(value) {
      this.form.code = (value || '').toUpperCase()
    },
    handleSearchCondition() {
      const condition = {}
      Object.keys(this.searchCondition).forEach((field) => {
        const value = this.searchCondition[field]
        if (value !== null && value !== '' && value !== undefined) {
          condition[field] = value
        }
      })
      // 分页 + 排序
      Object.assign(condition, this.pageData, { orderBy: this.orderBy })
      return condition
    },
    handleQuery() {
      this.pageData.current_page = 1
      this.getList()
      // 缓存筛选条件，刷新后可以继续使用
      sessionStorage.setItem(this.pageSearchKey, JSON.stringify(this.searchCondition))
    },
    handleReset() {
      this.searchCondition = { type: null, name: null, code: null, state: null }
      this.handleQuery()
    },
    async getList() {
      this.loading = true
      this.dataList = []
      try {
        const params = this.handleSearchCondition()
        const { list, pages } = await this.$api.getMiniAppList(params)
        this.dataList = list || []
        this.pageData.current_page = (pages && pages.current_page) || 1
        this.pageData.page_size = (pages && pages.page_size) || 100
        this.total = (pages && pages.total) || 0
      } finally {
        this.loading = false
      }
    },
    // 应用类型 / 状态 / 配置参考字段
    async getMeta(type) {
      const { types, states, config_fields: configFields } = await this.$api.getMiniAppMeta(type)
      this.typeOptions = types || []
      this.stateOptions = states || []
      this.configFields = configFields || {}
    },
    // 新增
    async handleAdd() {
      this.title = '新增应用'
      this.form = {
        type: this.typeOptions.length > 0 ? this.typeOptions[0].id : null,
        name: '',
        code: '',
        state: 1
      }
      this.configRows = []
      this.jsonMode = false
      this.jsonText = ''
      await this.handleTypeChange(this.form.type, false)
      this.open = true
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate()
      })
    },
    // 修改
    async handleEdit(row) {
      this.title = '修改应用'
      const { info } = await this.$api.getMiniAppInfo(row.id)
      const data = info || row
      this.form = {
        id: data.id,
        type: data.type,
        name: data.name,
        code: data.code,
        state: data.state
      }
      this.configRows = this.buildConfigRows(data.config || {})
      this.jsonMode = false
      this.jsonText = ''
      await this.handleTypeChange(this.form.type, false)
      this.open = true
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate()
      })
    },
    // 应用类型变更：刷新常用配置项
    async handleTypeChange(type, addFields = true) {
      await this.getMeta(type)
      if (addFields) this.applyConfigFields()
    },
    buildConfigRows(config) {
      return Object.keys(config || {}).map((key) => {
        const value = config[key]
        return { key: key, value: (value === null || value === undefined) ? '' : String(value) }
      })
    },
    addConfigRow() {
      this.configRows.push({ key: '', value: '' })
    },
    removeConfigRow(index) {
      this.configRows.splice(index, 1)
    },
    // 填充常用配置项（已存在的不覆盖）
    applyConfigFields() {
      const exists = this.configRows.map(_ => _.key)
      Object.keys(this.configFields || {}).forEach((key) => {
        if (exists.indexOf(key) === -1) {
          this.configRows.push({ key: key, value: '' })
        }
      })
    },
    // 表单 / json 模式切换
    toggleJsonMode() {
      if (!this.jsonMode) {
        this.jsonText = JSON.stringify(this.collectConfigFromRows(), null, 2)
        this.jsonMode = true
      } else {
        const config = this.parseJsonText()
        if (config === false) return
        this.configRows = this.buildConfigRows(config)
        this.jsonMode = false
      }
    },
    collectConfigFromRows() {
      const config = {}
      this.configRows.forEach((row) => {
        const key = (row.key || '').trim()
        if (key !== '') config[key] = row.value
      })
      return config
    },
    parseJsonText() {
      if (!this.jsonText || this.jsonText.trim() === '') return {}
      try {
        const config = JSON.parse(this.jsonText)
        if (config === null || typeof config !== 'object' || Array.isArray(config)) {
          this.$message.warning('应用配置需为 json 对象')
          return false
        }
        return config
      } catch (e) {
        this.$message.warning('应用配置 json 格式不正确')
        return false
      }
    },
    // 提交
    submitForm() {
      this.$refs.form.validate((valid) => {
        if (!valid) return
        let config
        if (this.jsonMode) {
          config = this.parseJsonText()
          if (config === false) return
        } else {
          config = this.collectConfigFromRows()
        }
        const params = Object.assign({}, this.form, {
          code: (this.form.code || '').toUpperCase(),
          config: config
        })
        this.loadingCommit = true
        this.$api.saveMiniApp(params).then((id) => {
          if (id) {
            this.$message.success(this.form.id ? '修改成功' : '新增成功')
            this.open = false
            this.getList()
          } else {
            this.$message.error('操作失败')
          }
        }).finally(() => {
          this.loadingCommit = false
        })
      })
    },
    // 启用 / 停用
    handleChangeState(row) {
      const state = row.state === 1 ? 0 : 1
      const text = state === 1 ? '启用' : '停用'
      this.$confirm(`此操作将${text}应用【${row.name}】, 是否继续?`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        await this.$api.changeMiniAppState(row.id, state)
        this.$message.success(`${text}成功`)
        this.getList()
      }).catch(() => {
        this.$message({ type: 'info', message: `已取消${text}` })
      })
    },
    // 删除
    handleDelete(row, index) {
      this.$confirm(`此操作将删除应用【${row.name}】, 是否继续?`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        await this.$api.deleteMiniApp(row.id)
        this.dataList.splice(index, 1)
        this.$message.success('删除成功')
        this.getList()
      }).catch(() => {
        this.$message({ type: 'info', message: '已取消删除' })
      })
    },
    // 配置详情
    handleViewConfig(row) {
      this.currentConfig = row
      this.showSecret = false
      this.openConfig = true
    }
  },
  async created() {
    if (sessionStorage.getItem(this.pageSearchKey)) {
      this.searchCondition = JSON.parse(sessionStorage.getItem(this.pageSearchKey))
    }
    await this.getMeta()
    this.handleQuery()
  }
}
</script>

<style scoped>
.page-intro {
  margin: 4px 0 6px;
  padding: 10px 14px;
  background: #f5f7fa;
  border-left: 3px solid #ff3176;
  border-radius: 4px;
}

.page-intro__title {
  margin-bottom: 4px;
  color: #303133;
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
}

.page-intro__desc {
  color: #606266;
  font-size: 12px;
  line-height: 20px;
}

.config-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.config-tip {
  color: #909399;
  font-size: 12px;
  line-height: 20px;
}

.config-pre {
  margin: 0;
  padding: 10px;
  max-height: 420px;
  overflow: auto;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 12px;
  line-height: 20px;
}
</style>
